import React, { type ReactNode } from 'react';
import type { Brand } from '@/domainLogic/index.ts';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import { HEADER_HEIGHT, SEARCH_WIDGET_HEIGHT } from '@/app/common/constants.ts';
import GenericStickyOnScrollUp from '@/app/common/components/StickyOnScrollUp';
import StickyNavFilters from '@/app/common/components/StickyNavBar/StickyNavFilters.tsx';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider';
import { useHeaderHeight } from '@/app/common/headerFooter/headerHeight.ts';

export type StickyOnScrollUpProps = {
  brand: Brand;
  children: ReactNode;
};

const StickyOnScrollUp = ({ brand, children }: StickyOnScrollUpProps) => {
  const { isMobile, externalHeaderEnabled } = useCommonClient();
  const { commonData, localization } = usePageWrapperData();
  const { isApp } = commonData;
  // MPH-395 workaround (see CSWAppContainer in styled.ts): with the standalone
  // app CSW the revealed header is the CSW container, so the filters stick below
  // its height — HEADER_HEIGHT is 0 in the app.
  const showsAppCsw = isApp && !!localization?.configValuesApp?.appShowCsw && !externalHeaderEnabled;
  // The ContentHub header is two rows tall when stacked (see headerHeight.ts); in the app it is hidden.
  const measuredHeaderHeight = useHeaderHeight(HEADER_HEIGHT(brand, isMobile, isApp));
  const contentHubHeaderHeight = isApp ? 0 : measuredHeaderHeight;

  if (isMobile) {
    return (
      <StickyNavFilters
        topOffset={
          showsAppCsw
            ? SEARCH_WIDGET_HEIGHT
            : externalHeaderEnabled
              ? HEADER_HEIGHT(brand, isMobile, isApp) + SEARCH_WIDGET_HEIGHT
              : contentHubHeaderHeight
        }
      >
        {children}
      </StickyNavFilters>
    );
  }

  return (
    <GenericStickyOnScrollUp
      topOffset={externalHeaderEnabled ? HEADER_HEIGHT(brand, false, isApp) : contentHubHeaderHeight}
    >
      {children}
    </GenericStickyOnScrollUp>
  );
};

export default StickyOnScrollUp;
